import React, { useState, useEffect, useCallback } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { X } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/use-toast";
import {
  API,
  fileUpload,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { encrypt, formatNumber, padWithLeadingZeros } from "../../lib/Misc";
import { useOrganization } from "../../lib/context/OrganisationContext";
import Spin from "../../components/Spin";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import BootstrapTable from "react-bootstrap-table-next";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

const CheckoutForm = ({
  plan,
  coupons,
  trustId,
  updateSubscription,
  setOpen,
  isOnBoard,
  isAMLCTF,
  isAddOn,
  fetchBillingData,
  fetchStripProducts,
  paymentMethods,
  currentSub,
  setPromptOnBoard,
  selected
}) => {
  let paymentMethod = paymentMethods?.map((item) => {
    return { ...item, value: item?.id };
  });

  let activePlan = isAddOn ? selected?.price_data : plan?.prices?.data?.find((item) => item.active === true);
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [gst, setGst] = useState(0);
  const [amountExc, setAmountExc] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [subtotal, setSubtotal] = useState(plan.amount / 100); // Default subtotal is the plan's amount
  const [total, setTotal] = useState(plan.amount / 100);
  const [couponError, setCouponError] = useState(false); // Flag for invalid coupon error
  const stripe = useStripe();
  const elements = useElements();
  // eslint-disable-next-line no-unused-vars
  const [selectedListCard, setSelectedListCard] = useState("");
  const [useExistingCard, setUseExistingCard] = useState(false);

  const [upcomingInvoice, setUpcomingInvoice] = useState();
  const [infoUpcomingInvoiceModal, setInfoUpcomingInvoiceModal] =
    useState(false);

  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const { organizationData, updateOrganizationData } = useOrganization();

  const [cardComplete, setCardComplete] = useState(false);

  const GST_PERCENTAGE = 0.1; // GST is 10%

  useEffect(() => {
    const gstAmount = (subtotal - discount) * GST_PERCENTAGE;
    const amountExcTotal = subtotal - discount;
    setAmountExc(amountExcTotal);
    setGst(gstAmount);
    setTotal(subtotal - discount + gstAmount);
  }, [subtotal, discount]);

  const handleApplyCoupon = async () => {
    validatePromoCode();
    if (
      plan?.metadata?.type !== "support_plan" &&
      currentSub?.status !== "canceled" &&
      currentSub?.subscription &&
      currentSub?.subscription !== undefined &&
      isAddOn === false
    ) {
      fetchUpcomingInvoice();
    }
  };

  const sendPayment = async (error, paymentMethodId = null, quantity) => {
    if (paymentMethodId) {
      setLoading(true);
      let values = {};
      values.price_id = activePlan.id;
      values.payment_method_id = paymentMethodId;
      values.coupon_code_id = coupon ? coupon : "";
      values.quantity = quantity ? quantity : "";
      values.organization_id = organizationData?.organizationId;
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        formData.append([key], values[key]);
      });

      formData.append("onboarding", isOnBoard ? 1 : 0);
      formData.append("is_aml_ctf", isAMLCTF ? 1 : 0);
      // fetchBillingData();

      try {
        let response = await fileUpload(
          isAddOn
            ? API.ADD_ON_SUBSCRIPTION
            : updateSubscription && plan?.metadata?.type !== "support_plan"
              ? API.UPDATE_SUBSCRIPTION
              : API.ORG_SUBSCRIPTION,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        if (response.data.status === 200) {
          setLoading(false);
          setOpen(false);


          updateOrganizationData(response?.data?.organization);
          if (isAddOn) {
            navigate('/general-settings?tab=subscription-details')
          } else {
            await fetchBillingData();
            await fetchStripProducts();
            localStorage.setItem("ts-subscription", encrypt("1"));
          }
          if (!isOnBoard && !currentSub?.support_plan?.price_id) {
            setPromptOnBoard && setPromptOnBoard(true);
          }
        }
      } catch (e) {
        setLoading(false);
      }
    } else {
      setLoading(false);
      toast({
        variant: "destructive",
        description: error?.message ? error?.message : error,
        duration: 3000,
      });
    }
  };

  const handleStripePayment = async () => {
    if (selectedListCard) {
      sendPayment("", selectedListCard, 1);
    } else {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
      });

      await sendPayment(error, paymentMethod?.id ? paymentMethod?.id : null, 1);
    }
  };



  const validatePromoCode = () => {
    for (let item of coupons) {
      if (item?.id === coupon) {
        let condition = isAddOn ? item?.applies_to?.products.find(
          (item) => String(item) === String(plan?.plan?.id),
        ) : item?.applies_to?.products.find(
          (item) => String(item) === String(plan?.id),
        )
        if (
          condition
        ) {
          if (item?.percent_off) {
            setDiscount(Number((item?.percent_off * subtotal) / 100));
          } else {
            setDiscount(item?.amount_off);
          }

          setCouponError(false);
          return;
        }
      }
    }
    setCouponError(true);
    toast({
      variant: "destructive",
      description: "Coupon code is not valid",
      duration: 3000,
    });
  };

  // *API For Fetching the Upcoming invoice details - Start
  const fetchUpcomingInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      if (organizationData?.organizationId) {
        formData.append("organization_id", organizationData?.organizationId);
      }
      formData.append("price_id", activePlan.id);
      // formData.append("subscription_id", subscriptionDetail?.subscription_id);
      formData.append("coupon_code_id", coupon ? coupon : "");
      formData.append("quantity", 1);
      const { data } = await fileUpload(API.GET_UPCOMING_INVOICE, formData);
      if (data) {
        setLoading(false);
        setUpcomingInvoice(data.data);
      } else {
        setLoading(false);
        setUpcomingInvoice();
      }
    } catch (error) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlan.id, organizationData?.organizationId]);

  useEffect(() => {
    if (
      plan?.metadata?.type !== "support_plan" &&
      currentSub?.status !== "canceled" &&
      currentSub?.subscription &&
      currentSub?.subscription !== undefined &&
      isAddOn === false
    ) {
      fetchUpcomingInvoice();
    }
  }, [fetchUpcomingInvoice, plan?.metadata?.type, currentSub, isAddOn]);

  const toggleUpcomingInvoiceList = () => {
    setInfoUpcomingInvoiceModal(!infoUpcomingInvoiceModal);
  };

  const dollarNumberFormat = (data) => {
    if (Number(data) < 0) {
      return `-$${formatNumber(Math.abs(Number(data)))}`;
    } else {
      return `$${formatNumber(Math.abs(Number(data)))}`;
    }
  };

  const viewJournalDataGenerator = (quantity) => {
    const items = [];
    let mainData = upcomingInvoice?.lines?.data;
    let discountTotalValue = 0;
    let SubTotalValue = 0;
    let TaxValue = 0;
    let TotalValue = 0;

    let planSubTotal = 0;
    let planTaxValue = 0;
    let planDiscountValue = 0;
    let planTotalValue = 0;
    // let lastItem = 0;

    for (let i = 0; i < quantity; i++) {
      SubTotalValue = SubTotalValue + Number(mainData[i]?.amount / 100);
      TaxValue = TaxValue + Number(mainData[i]?.taxes[0]?.amount / 100);
      discountTotalValue =
        discountTotalValue +
        Number(mainData[i]?.discount_amounts?.[0]?.amount / 100);
      TotalValue =
        TotalValue +
        (Number(mainData[i]?.amount / 100) +
          Number(mainData[i]?.taxes[0]?.amount / 100));

      var startDate = new Date(Number(mainData[i]?.period?.start) * 1000);
      var formattedStartDate =
        padWithLeadingZeros(Number(startDate.getUTCDate()), 2) +
        "-" +
        padWithLeadingZeros(Number(startDate.getUTCMonth() + 1), 2) +
        "-" +
        startDate.getUTCFullYear();
      var endDate = new Date(Number(mainData[i]?.period?.end) * 1000);
      var formattedEndDate =
        padWithLeadingZeros(Number(endDate.getUTCDate()), 2) +
        "-" +
        padWithLeadingZeros(Number(endDate.getUTCMonth() + 1), 2) +
        "-" +
        endDate.getUTCFullYear();

      let planSubTotalCalc = 0;

      planSubTotal = Number(planSubTotal) + Number(planSubTotalCalc);

      let planTaxCalc = 0;

      planTaxValue = Number(planTaxValue) + Number(planTaxCalc);

      let planDiscountCalc = 0;

      planDiscountValue = Number(planDiscountValue) + Number(planDiscountCalc);

      let planTotalCalc = 0;

      planTotalValue = Number(planTotalValue) + Number(planTotalCalc);

      items.push({
        key: mainData[i]?.id,
        start_date: formattedStartDate ? formattedStartDate : "-",
        end_date: formattedEndDate ? formattedEndDate : "-",
        description: mainData[i]?.description ? mainData[i]?.description : "-",
        plan_sub_total: planSubTotalCalc
          ? dollarNumberFormat(Number(planSubTotalCalc))
          : "-",
        plan_gst: planTaxCalc ? dollarNumberFormat(Number(planTaxCalc)) : "-",
        plan_discount: planDiscountCalc
          ? dollarNumberFormat(Number(planDiscountCalc))
          : "-",
        plan_total: planTotalCalc
          ? dollarNumberFormat(Number(planTotalCalc))
          : "-",
        sub_total: mainData[i]?.amount
          ? dollarNumberFormat(Number(mainData[i]?.amount / 100))
          : "$0.00",
        tax: mainData[i]?.taxes[0]?.amount
          ? dollarNumberFormat(Number(mainData[i]?.taxes[0]?.amount / 100))
          : "$0.00",
        discount: mainData[i]?.discount_amounts?.[0]?.amount
          ? dollarNumberFormat(
            Number(mainData[i]?.discount_amounts?.[0]?.amount / 100),
          )
          : "$0.00",
        total_amount: mainData[i]?.amount
          ? dollarNumberFormat(
            Number(mainData[i]?.amount / 100) +
            Number(mainData[i]?.taxes[0]?.amount / 100),
          )
          : 0,
      });

      if (i === quantity - 1) {
        items.push({
          description: (
            <div style={{ textAlign: "right" }}>
              <strong>Prorated amlount to be added in the next invoice:</strong>
            </div>
          ),
          sub_total: (
            <strong>{dollarNumberFormat(Number(SubTotalValue))}</strong>
          ),
          tax: <strong>{dollarNumberFormat(Number(TaxValue))}</strong>,
          discount: (
            <strong>
              {discountTotalValue
                ? dollarNumberFormat(Number(discountTotalValue))
                : "$0.00"}
            </strong>
          ),
          total_amount: (
            <strong>{dollarNumberFormat(Number(TotalValue))}</strong>
          ),
        });
      }
    }
    return items;
  };
  const viewJournalData = viewJournalDataGenerator(
    upcomingInvoice?.lines?.data?.length,
  );

  const viewColumns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "start_date",
      text: "Start Date",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "end_date",
      text: "End Date",
    },

    // {
    //   headerClasses: THClasses,
    //   classes: TDClasses,
    //   dataField: "plan_sub_total",
    //   text: "Plan Sub Total",
    //   headerStyle: {
    //     textAlign: "right",
    //   },
    //   style: {
    //     textAlign: "right",
    //   },
    // },
    // {
    //   headerClasses: THClasses,
    //   classes: TDClasses,
    //   dataField: "plan_gst",
    //   text: "Plan GST",
    //   headerStyle: {
    //     textAlign: "right",
    //   },
    //   style: {
    //     textAlign: "right",
    //   },
    // },
    // {
    //   headerClasses: THClasses,
    //   classes: TDClasses,
    //   dataField: "plan_discount",
    //   text: "Plan Discount",
    //   headerStyle: {
    //     textAlign: "right",
    //   },
    //   style: {
    //     textAlign: "right",
    //   },
    // },
    // {
    //   headerClasses: THClasses,
    //   classes: TDClasses,
    //   dataField: "plan_total",
    //   text: "Plan Total",
    //   headerStyle: {
    //     textAlign: "right",
    //   },
    //   style: {
    //     textAlign: "right",
    //   },
    // },

    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "description",
      text: "Description",
    },

    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "sub_total",
      text: "Sub Total",
      headerStyle: {
        textAlign: "right",
      },
      style: {
        textAlign: "right",
      },
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "tax",
      text: "Tax",
      headerStyle: {
        textAlign: "right",
      },
      style: {
        textAlign: "right",
      },
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "discount",
      text: "Discount",
      headerStyle: {
        textAlign: "right",
      },
      style: {
        textAlign: "right",
      },
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "total_amount",
      text: "Amount",
      headerStyle: {
        textAlign: "right",
      },
      style: {
        textAlign: "right",
      },
    },
  ];

  // *API For Fetching the Upcoming invoice details - End

  return (
    <>
      {loading && <Spin fullscreen={true} />}
      <form className="space-y-4">
        {!useExistingCard && (
          <div className="space-y-2">
            <h3 className="text-xl font-semibold">Payment Details</h3>
            <CardElement
              className="pl-3 pr-1 py-2 border border-gray-300 rounded-md bg-white"
              options={{ hidePostalCode: true }}
              onChange={(event) => {
                setCardComplete(event.complete);
              }}
            />
          </div>
        )}

        {paymentMethod?.length > 0 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="use-existing"
                  checked={useExistingCard}
                  onCheckedChange={(checked) => {
                    setUseExistingCard(!!checked);
                    if (!checked) setSelectedListCard("");
                  }}
                />
                <Label htmlFor="use-existing">
                  Use Existing Payment Method
                </Label>
              </div>

              {useExistingCard && paymentMethod?.length > 0 && (
                <RadioGroup
                  value={selectedListCard}
                  onValueChange={setSelectedListCard}
                  className="space-y-2 pl-6 pt-2"
                >
                  {paymentMethod.map((card) => (
                    <div key={card.id} className="flex items-center space-x-2">
                      <RadioGroupItem value={card.id} id={card.id} />
                      <Label htmlFor={card.id} className="text-sm">
                        {card.brand.toUpperCase()} **** {card.last4} (exp{" "}
                        {card.expiry})
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <h4 className="text-lg font-semibold">Apply Coupon</h4>
          {!discount && (
            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="Enter Coupon Code"
                className="flex-1 bg-white text-black"
              />
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  handleApplyCoupon();
                }}
                className="pt-1"
              >
                Apply
              </Button>
            </div>
          )}

          {couponError && (
            <div className="flex items-center text-red-500">
              <X className="w-4 h-4 mr-2" />
              Invalid Coupon Code
            </div>
          )}

          {discount > 0 && !couponError && (
            <div className="text-green-500">
              Discount Applied: <span className="text-primary">{coupon}</span>{" "}
              <span>
                <Button
                  variant="destructive"
                  className="p-2 h-6 ml-4"
                  onClick={() => {
                    setDiscount(0);
                    setCoupon("");
                    setCouponError(false);
                  }}
                >
                  Clear
                </Button>
              </span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <h4 className="text-lg font-semibold">Summary</h4>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Discount</span>
              <span>
                {discount ? "-" : ""}${discount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Amount (Exc. GST)</span>
              <span>${amountExc.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>GST (10%)</span>
              <span>${gst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold">
              <span>
                Total{" "}
                {upcomingInvoice && (
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    onClick={() => toggleUpcomingInvoiceList()}
                  />
                )}
              </span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <Button
          onClick={(e) => {
            e.preventDefault();
            setLoading(true);
            handleStripePayment();
          }}
          disabled={
            !stripe ||
            !elements ||
            (!selectedListCard && !cardComplete) ||
            loading
          }
          className="w-full mt-4 bg-green-600 text-white"
        >
          Pay Now
        </Button>
      </form>

      {infoUpcomingInvoiceModal && (
        <ResponsiveDrawerForm
          description={""}
          title={"Upcoming Invoice"}
          handleOpen={() => { }}
          handleClose={() => {
            setInfoUpcomingInvoiceModal(false);
          }}
          className={"md:w-[90vw]"}
          open={infoUpcomingInvoiceModal}
          buttonClassName={""}
          setOpen={setInfoUpcomingInvoiceModal}
          read={true}
        >
          <div className="custom-table">
            <div className={"table-body-class w-full"}>
              <BootstrapTable
                keyField="key"
                data={viewJournalData}
                columns={viewColumns}
                noDataIndication="No Data Found"
                rowClasses={TRClasses}
                classes={TableClasses}
                wrapperClasses={TableWrapClasses}
              />
            </div>
          </div>
        </ResponsiveDrawerForm>
      )}
    </>
  );
};

export default CheckoutForm;
